import type { Messages } from '../catalogue.en'

/**
 * The doctor dashboard, including the board.
 *
 * Composed into the public catalogue by `catalogue.ru.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const dashboard: Partial<Messages> = {
  'dashboard.title': 'Панель управления',

  'dashboard.boardSections': 'Разделы панели управления',
  'dashboard.boardSection': '{title} — {unit}: {count}',
  'dashboard.boardSectionSeries': '{title}. {series}. {caption}',
  'dashboard.boardSeriesFigure': '{label}: {count}',

  'dashboard.attentionTitle': 'Требует внимания',
  'dashboard.attentionUnit': 'элементов',
  'dashboard.attentionLink': 'Показать все',
  'dashboard.attentionFooter': 'Показать все элементы',

  'dashboard.dayTitle': 'Моё расписание на сегодня',
  'dashboard.dayUnit': 'приёмов',
  'dashboard.dayLink': 'Открыть расписание',
  'dashboard.dayShown': 'Показано {shown} из {total} приёмов',
  'dashboard.dayFilterAll': 'Все статусы',
  'dashboard.dayLoading': 'Загрузка расписания на сегодня',
  'dashboard.dayUnavailable': 'Доступ к расписанию недоступен',
  'dashboard.clinicDayTitle': 'Расписание клиники сегодня',
  'dashboard.dayEmpty': 'Сегодня приёмов нет',
  'dashboard.dayEmptyDetail': 'В расписании нет приёмов на клинический день, определённый сервером.',
  'dashboard.dayFilterEmpty': 'Нет приёмов с этим статусом',
  'dashboard.dayLoadFailed': 'Не удалось загрузить расписание на сегодня.',
  'dashboard.dayStale': 'Не удалось обновить расписание.',
  'dashboard.dayLastConfirmed': 'Последнее подтверждение в {time}',
  'dashboard.dayRetry': 'Повторить',

  'dashboard.myTasksTitle': 'Мои задачи',
  'dashboard.myTasksUnit': 'в работе',
  'dashboard.myTasksLink': 'Открыть задачи',
  'dashboard.myTasksFooter': 'Показать завершённые задачи',

  'dashboard.followUpsTitle': 'Открытые наблюдения',
  'dashboard.followUpsUnit': 'в ожидании',
  'dashboard.followUpsLink': 'Проверить наблюдения',
  'dashboard.followUpsFooter': 'Показать все наблюдения',

  'dashboard.eventsTitle': 'Расписание моих пациентов',
  'dashboard.eventsUnit': 'событий',
  'dashboard.eventsLink': 'Показать события пациентов',
  'dashboard.eventsFooter': 'Открыть полное расписание',

  'dashboard.chooseDay': 'Выберите день',
  'dashboard.filter': 'Фильтр',
  'dashboard.filters': 'Фильтры',
  'dashboard.sortDueDate': 'Срок',
  'dashboard.scopeAllFollowUps': 'Все наблюдения',
  'dashboard.viewList': 'Список',
  'dashboard.viewTimeline': 'Хронология',
  'dashboard.columnFollowUp': 'Наблюдение',
  'dashboard.columnDue': 'Срок',
  'dashboard.columnEvent': 'Событие',
  'dashboard.columnLocation': 'Место',
  'dashboard.taskScopeAll': 'Все ({count})',
  'dashboard.taskScopeClinical': 'Клинические ({count})',
  'dashboard.taskScopeAdmin': 'Административные ({count})',
  'dashboard.taskGroupOverdue': 'Просроченные ({count})',
  'dashboard.taskGroupDueToday': 'Срок сегодня ({count})',
  'dashboard.taskGroupUpcoming': 'Предстоящие ({count})',

  'dashboard.personMeta': 'Возраст: {age} · {sex}',
  'dashboard.minutes': '{count} мин',
  'dashboard.room': 'Кабинет {number}',
  'dashboard.consultRoom': 'Кабинет приёма {number}',
  'dashboard.ward': 'Палата {number}',
  'dashboard.phlebotomy': 'Забор крови',
  'dashboard.televisit': 'Телевизит',
  'dashboard.dueAt': 'Срок {when}',
  'dashboard.yesterday': 'Вчера',
  'dashboard.tomorrow': 'Завтра',
  'dashboard.inDays': 'Через {count} дн.',
  'dashboard.daysAgo': '{count} дн. назад',

  'dashboard.stateNew': 'Новое',
  'dashboard.stateHigh': 'Высокий',
  'dashboard.stateReview': 'На проверку',
  'dashboard.stateUnsigned': 'Не подписано',
  'dashboard.stateOverdue': 'Просрочено',
  'dashboard.stateResponse': 'Ответ',
  'dashboard.stateUpcoming': 'Предстоит',
  'dashboard.stateScheduled': 'Запланировано',
  'dashboard.stateInProgress': 'В работе',
  'dashboard.stateCompleted': 'Завершено',
  'dashboard.stateAwaiting': 'Ожидание',
  'dashboard.stateAwaitingResult': 'Ожидание результата',
  'dashboard.stateAwaitingPatient': 'Ожидание пациента',
  'dashboard.stateDueSoon': 'Скоро срок',
  'dashboard.stateDueToday': 'Срок сегодня',

  'dashboard.attentionNewResult': 'Новый результат анализа',
  'dashboard.attentionNewResultDetail': 'Общий анализ крови',
  'dashboard.attentionOutOfRange': 'Результат вне референтного диапазона',
  'dashboard.attentionOutOfRangeDetail': 'Калий',
  'dashboard.attentionNewDocument': 'Новый документ для проверки',
  'dashboard.attentionNewDocumentDetail': 'Отчёт по визуализации',
  'dashboard.attentionUnsignedNote': 'Неподписанная запись',
  'dashboard.attentionUnsignedNoteDetail': 'Дневниковая запись',
  'dashboard.attentionOverdueFollowUp': 'Просроченное наблюдение',
  'dashboard.attentionOverdueFollowUpDetail': 'Наблюдение просрочено на {count} дн.',
  'dashboard.attentionMessage': 'Пациент ждёт вашего ответа',
  'dashboard.attentionMessageDetail': 'Сообщение с портала',


  'dashboard.followUpResult': 'Ожидание результата анализа',
  'dashboard.followUpResultDetail': 'Витамин B12',
  'dashboard.followUpImagingDisk': 'Пациент должен прислать диск с исследованием',
  'dashboard.followUpImagingDiskDetail': 'Снимок поясничного отдела позвоночника',
  'dashboard.followUpRecheck': 'Повторная проверка через три месяца',
  'dashboard.followUpRecheckDetail': 'Наблюдение по диабету',
  'dashboard.followUpConsult': 'Консультацию нужно назначить',
  'dashboard.followUpConsultDetail': 'Обсудить варианты лечения',
  'dashboard.followUpBiopsy': 'Проверить заключение по биопсии',
  'dashboard.followUpBiopsyDetail': 'Ожидается заключение патоморфолога',
  'dashboard.followUpReply': 'Сообщение пациента ждёт ответа',
  'dashboard.followUpReplyDetail': 'Сообщение пациента с портала',

  'dashboard.eventImaging': 'Сканирование головного мозга',
  'dashboard.eventImagingCategory': 'Визуализация',
  'dashboard.eventConsult': 'Консультация специалиста',
  'dashboard.eventConsultCategory': 'Консультация',
  'dashboard.eventBloodTest': 'Анализ крови',
  'dashboard.eventBloodTestCategory': 'Лабораторная диагностика',
  'dashboard.eventAdmission': 'Госпитализация на операцию',
  'dashboard.eventAdmissionCategory': 'Ортопедия',
  'dashboard.eventTeleconsultation': 'Телеконсультация',
  'dashboard.eventTeleconsultationCategory': 'Наблюдение',
  'dashboard.eventDischarge': 'Визит при выписке',
  'dashboard.eventDischargeCategory': 'Терапия',
  'dashboard.placeImaging': 'Центр лучевой диагностики',
  'dashboard.placeClinic': 'Клиника',
  'dashboard.placeLaboratory': 'Лаборатория',
  'dashboard.placeHospital': 'Больница',
  'dashboard.placeVirtual': 'Дистанционный приём',

  'dashboard.nextAppointment': 'Следующий приём',
  'dashboard.railTitle': 'Сводка',
  'dashboard.dayOverview': 'Обзор дня',
  'dashboard.overviewAppointments': 'Приёмы',
  'dashboard.overviewInPerson': 'Очно',
  'dashboard.overviewTelevisits': 'Телевизиты',
  'dashboard.overviewCompleted': 'Завершено',
  'dashboard.overviewCheckedIn': 'Пришли',
  'dashboard.overviewWindow': '{from} – {to}',
  'dashboard.todaysPatients': 'Пациенты сегодня',
  'dashboard.railMore': 'Ещё {count}',
  'dashboard.taskSummary': 'Сводка по задачам',
  'dashboard.summaryTotal': 'всего',
  'dashboard.summaryTotalRow': 'Всего',
  'dashboard.summaryWaitingOnOthers': 'Ждут других',
  'dashboard.followUpSummary': 'Сводка по наблюдениям',
  'dashboard.clinicTimeNote': 'Всё время указано в часовом поясе клиники',

  'dashboard.quickActions': 'Быстрые действия',
  'dashboard.actionNewLead': 'Новая заявка',
  'dashboard.actionNewLeadDetail': 'Начать регистрацию пациента',
  'dashboard.actionNewTask': 'Новая задача',
  'dashboard.actionNewTaskDetail': 'Создать задачу',
  'dashboard.actionSendMessage': 'Отправить сообщение',
  'dashboard.actionSendMessageDetail': 'Написать пациенту или врачу',
  'dashboard.actionUploadDocument': 'Загрузить документ',
  'dashboard.actionUploadDocumentDetail': 'Добавить файл',
  'dashboard.actionCallPatient': 'Позвонить пациенту',
  'dashboard.actionCallPatientDetail': 'Записать звонок',
  'dashboard.actionCreateFollowUp': 'Создать наблюдение',
  'dashboard.actionMessagePatient': 'Написать пациенту',
  'dashboard.actionSendReminder': 'Отправить напоминание',
}
